import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Alert, AlertTitle, Button, Snackbar } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { AppendIfPrefixExists } from "modules/sitePaths";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";
import {
  type CartSwitchSummary,
  markCartSwitchSummaryAnnounced,
  readCartSwitchSummary,
} from "./cartSwitchSummary";

const plural = (count: number, word: string) =>
  `${count} ${word}${count === 1 ? "" : "s"}`;

export const CART_NOT_RECALCULATED_MESSAGE =
  "While impersonating, the cart is not recalculated. It still shows this user's items and prices from their own storefront.";
export const CART_WILL_NOT_RECALCULATE_MESSAGE =
  "While impersonating, the cart is not recalculated. It will keep this user's items and prices from their own storefront.";
export const CART_MIGRATION_FAILED_MESSAGE =
  "We could not check your cart against this storefront's catalog. Review item availability and prices before checking out.";

export function describeCartSwitch(summary: CartSwitchSummary): string {
  if (summary.cartNotRecalculated) {
    return CART_NOT_RECALCULATED_MESSAGE;
  }
  if (summary.cartMigrationFailed) {
    return CART_MIGRATION_FAILED_MESSAGE;
  }
  const parts = [
    summary.removedCount > 0 &&
      `${plural(summary.removedCount, "item")} ${summary.removedCount === 1 ? "is" : "are"} not available and ${summary.removedCount === 1 ? "was" : "were"} removed`,
    summary.repriced.length > 0 &&
      `${plural(summary.repriced.length, "price")} changed`,
  ].filter(Boolean);
  return `${parts.join(", and ")}.`;
}

/** Tells the user, wherever the post-switch reload lands them, that the switch changed their cart. */
export function StorefrontSwitchResultNotice() {
  const { user } = useAuth0();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [summary, setSummary] = useState(() => {
    const stored = readCartSwitchSummary(user?.sub);
    return stored && !stored.announced ? stored : null;
  });

  useEffect(() => {
    if (pathname.endsWith("/cart")) {
      markCartSwitchSummaryAnnounced(user?.sub);
      setSummary(null);
      return;
    }
    const stored = readCartSwitchSummary(user?.sub);
    setSummary(stored && !stored.announced ? stored : null);
  }, [user?.sub, pathname]);

  // The cart page shows the full breakdown itself.
  if (!summary || pathname.endsWith("/cart")) {
    return null;
  }

  const dismiss = () => {
    markCartSwitchSummaryAnnounced(user?.sub);
    setSummary(null);
  };

  return (
    <Snackbar open anchorOrigin={{ vertical: "top", horizontal: "center" }}>
      <Alert
        severity={summary.cartMigrationFailed ? "warning" : "info"}
        sx={{ boxShadow: 3 }}
        action={
          <>
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                dismiss();
                navigate(AppendIfPrefixExists("/cart"));
              }}
              {...telemetryProps({
                name: "storefront_switch_view_cart",
                section: "storefront_switch_notice",
                label: "View cart",
              })}
            >
              View cart
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={dismiss}
              {...telemetryProps({
                name: "storefront_switch_notice_dismiss",
                section: "storefront_switch_notice",
                label: "Dismiss",
              })}
            >
              Dismiss
            </Button>
          </>
        }
      >
        <AlertTitle>
          {summary.cartNotRecalculated
            ? "Your cart was not updated for this storefront"
            : summary.cartMigrationFailed
              ? "Your cart could not be checked for this storefront"
              : "Your cart was updated for this storefront"}
        </AlertTitle>
        {describeCartSwitch(summary)}
      </Alert>
    </Snackbar>
  );
}
